import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ActionRole } from '@newroom-connect/library/enums';

import { InputComponent } from '../input.component';
import { IconComponent } from '../../icon/icon.component';

export interface ISegmentOption<T = unknown> {
  value: T;
  label?: string;
  icon?: string;
}

@Component({
  standalone: true,
  imports: [CommonModule, IconComponent],
  selector: 'nrc-input-segment',
  templateUrl: './input-segment.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputSegmentComponent),
      multi: true
    }
  ]
})
export class InputSegmentComponent<TOutput = string> extends InputComponent<TOutput> implements OnInit, OnChanges {
  @Input() public options: ISegmentOption<TOutput>[] = [];
  @Input() public value: TOutput | null = null;
  @Input() public type = 'REGULAR';

  @Output() public optionChangedEvent = new EventEmitter<ISegmentOption<TOutput>>();

  public selectedIndex = 0;

  public actionRole = ActionRole;

  /**
   *
   */
  public override ngOnInit(): void {
    super.ngOnInit();

    if (this.options.length > 0) {
      this.formControl.setValue(this.options[0].value);
    }
  }

  /**
   *
   * @param changes
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['formControl']) {
      const index = this.options.findIndex((option) => option.value === this.formControl.value);

      if (index >= 0) {
        this.selectOption(index);
      }
    }

    if (changes && changes['value'] && this.value !== null) {
      const index = this.options.findIndex((option) => option.value === this.value);

      if (index >= 0) {
        this.selectOption(index);
      }
    }
  }

  /**
   * Select an option where `selectedIndex` is set to the given option index and the form control is set to the option value.
   *
   * @param optionIndex The index of the option to select from the options array.
   */
  public selectOption(optionIndex: number): void {
    if (optionIndex < 0 || optionIndex >= this.options.length) {
      return;
    }

    this.selectedIndex = optionIndex;
    const selectedOption = this.options[optionIndex];

    this.optionChangedEvent.emit(selectedOption);

    this.formControl.setValue(selectedOption.value);
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
  }
}
